import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Settings, Package, Users, LayoutGrid,
  X, Maximize2, Minimize2, RefreshCw, AlertCircle,
  Scale, Edit2, ExternalLink, Check, Loader2, Plus, DollarSign, ArrowRight,
  Save, ListPlus,
} from 'lucide-react';
import { lotesService } from '../../services/lotesService';
import leiloesService from '../../services/leiloesService';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useLabelCurral } from '../../hooks/useLabelCurral';
import curralLayoutService from '../../services/curralLayoutService';
import { meiosPagamentoService } from '../../services/meiosPagamentoService';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_CONFIG = { currais: [], colunas: 12, usarCurralComoNumeroLote: true };

const STATUS_DOT = {
  FATURADO: 'bg-green-400',
  DEFESA:   'bg-red-400',
  NOVO:     'bg-blue-300',
  EDITADO:  'bg-orange-400',
};

const fmt = (v) =>
  v ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : null;

const fmtPeso = (v) =>
  v ? `${parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg/cab` : null;

const fmtPesoArroba = (v) => {
  if (!v) return null;
  const kg = parseFloat(v);
  const arroba = (kg / 30).toFixed(1);
  return `${kg.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}kg/${arroba}@`;
};

// ─── Curral card ─────────────────────────────────────────────────────────────

const CurralCard = ({ curral, selected, onClick, labelCurral }) => {
  if (!curral.ocupado) {
    return (
      <button
        onClick={onClick}
        title={`Curral ${curral.display} — Disponível · Clique para cadastrar lote`}
        className={`rounded-lg flex flex-col items-center justify-center select-none gap-0.5 transition-all duration-150
          ${selected
            ? 'bg-emerald-200 border-2 border-emerald-500 shadow-md scale-[1.05] z-10'
            : 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400 hover:scale-[1.03] hover:shadow-sm'
          }`}
        style={{ aspectRatio: '1 / 1.15' }}
      >
        <span className="text-emerald-800 font-black text-base leading-none">{curral.display}</span>
        <span className="text-emerald-500 text-[8px] uppercase tracking-widest font-medium">Livre</span>
        {selected && <Plus size={10} className="text-emerald-600 mt-0.5" />}
      </button>
    );
  }

  const totalAnimais = curral.lotes.reduce((s, l) => s + (l.qtd || 0), 0);
  const multiplos    = curral.lotes.length > 1;
  const primeiro     = curral.lotes[0];
  const vendedor     = primeiro?.vendedor?.nome?.split(' ')[0] || '';

  // Status flags — drive card color
  const temDefesa   = curral.lotes.some(l => l.status === 'DEFESA');
  const temFaturado = !temDefesa && curral.lotes.some(l => l.status === 'FATURADO');
  const temEditado  = !temDefesa && !temFaturado && curral.lotes.some(l => l.status === 'EDITADO');
  const comPeso     = curral.lotes.filter(l => l.peso && parseFloat(l.peso) > 0).length;
  const todosPeso   = comPeso === curral.lotes.length;
  const algumPeso   = comPeso > 0 && !todosPeso;

  const gradient = temDefesa
    ? 'bg-gradient-to-br from-red-600 via-red-600 to-red-700'
    : temFaturado
    ? 'bg-gradient-to-br from-green-700 via-green-700 to-green-800'
    : temEditado
    ? 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600'
    : todosPeso
    ? 'bg-gradient-to-br from-blue-500 via-blue-500 to-blue-600'
    : algumPeso
    ? 'bg-gradient-to-br from-blue-400 via-amber-600 to-amber-700'
    : 'bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700';

  const pesoLabel = !multiplos && primeiro?.peso ? fmtPesoArroba(primeiro.peso) : null;

  return (
    <button
      onClick={onClick}
      title={`Curral ${curral.display} — ${totalAnimais} animais`}
      className={`relative rounded-lg p-2 flex flex-col text-left transition-all duration-150
        ${gradient}
        ${selected
          ? 'ring-2 ring-white ring-offset-1 shadow-2xl scale-[1.07] z-10'
          : 'hover:scale-[1.04] hover:shadow-lg shadow-sm'
        }`}
      style={{ aspectRatio: '1 / 1.15' }}
    >
      {/* top row: status dots */}
      <div className="flex items-center justify-end">
        <div className="flex gap-0.5">
          {curral.lotes.slice(0, 3).map((l, i) => (
            <div key={i} className={`w-1 h-1 rounded-full ${STATUS_DOT[l.status] || 'bg-white/50'}`} />
          ))}
        </div>
      </div>

      {/* Destaque: número do curral */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <span className="text-white/60 text-[8px] uppercase tracking-wider font-medium">{labelCurral}</span>
        <span className="text-white font-black text-2xl leading-none tabular-nums drop-shadow">
          {curral.display}
        </span>
      </div>

      {/* bottom: qtd animais + sexo + lote */}
      <div className="text-center">
        <div className="text-white/90 font-bold text-sm leading-tight">
          {totalAnimais} {totalAnimais === 1 ? 'cab' : 'cab'}
        </div>
        {pesoLabel && <div className="text-white font-bold text-[11px] leading-tight">{pesoLabel}</div>}
        {(() => {
          const sexos = [...new Set(curral.lotes.map(l => l.sexo).filter(Boolean))];
          const sexoLabel = sexos.length === 1 ? sexos[0] : sexos.length > 1 ? 'Misto' : null;
          return sexoLabel ? (
            <div className="text-white/80 text-[10px] font-medium leading-tight">{sexoLabel}</div>
          ) : null;
        })()}
        <div className="text-white/60 text-[10px] truncate mt-0.5">
          {multiplos ? `${curral.lotes.length} lotes` : `Lote ${primeiro?.numero_lote}`}
        </div>
      </div>
    </button>
  );
};

// ─── Detail panel ────────────────────────────────────────────────────────────

const DetalhePanel = ({ curral, onClose, onSavePeso, onEditLote, onVender }) => {
  const [pesoEdit, setPesoEdit] = useState({});

  const iniciarEditar = (lote) => {
    setPesoEdit(prev => ({
      ...prev,
      [lote.id]: {
        ativo: true,
        valor: lote.peso ? String(parseFloat(lote.peso)) : '',
        saving: false,
      },
    }));
  };

  const cancelar = (loteId) => {
    setPesoEdit(prev => { const n = { ...prev }; delete n[loteId]; return n; });
  };

  const salvar = async (lote) => {
    const est = pesoEdit[lote.id];
    if (!est) return;
    const valorNum = parseFloat(est.valor.replace(',', '.'));
    if (isNaN(valorNum) || valorNum <= 0) return;
    setPesoEdit(prev => ({ ...prev, [lote.id]: { ...prev[lote.id], saving: true } }));
    await onSavePeso(lote.id, valorNum);
    setPesoEdit(prev => { const n = { ...prev }; delete n[lote.id]; return n; });
  };

  // Reset edit state when curral changes
  useEffect(() => { setPesoEdit({}); }, [curral.display]);

  return (
    <div className="w-80 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Curral</p>
          <h3 className="text-lg font-bold text-gray-900">{curral.display}</h3>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors">
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {curral.lotes.map((lote) => {
          const valor = lote.valor_venda_total
            ? fmt(lote.valor_venda_total)
            : lote.valor_venda_unidade
            ? fmt(lote.valor_venda_unidade * (lote.qtd || 1))
            : null;
          const est = pesoEdit[lote.id];
          const temPeso = lote.peso && parseFloat(lote.peso) > 0;

          return (
            <div key={lote.id} className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <span className="text-red-700 font-bold text-base">Lote {lote.numero_lote}</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  lote.status === 'FATURADO' ? 'bg-green-100 text-green-700' :
                  lote.status === 'DEFESA'   ? 'bg-red-100 text-red-700' :
                                               'bg-blue-100 text-blue-700'
                }`}>
                  {lote.status}
                </span>
              </div>

              {/* Animal info */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Package size={13} className="text-gray-400 flex-shrink-0" />
                  <span>{lote.qtd} {lote.tipo_animal?.nome}{lote.sexo ? ` · ${lote.sexo}` : ''}</span>
                </div>
                {lote.raca?.nome  && <div className="text-xs text-gray-500 ml-5">{lote.raca.nome}</div>}
                {lote.idade?.nome && <div className="text-xs text-gray-500 ml-5">{lote.idade.nome}</div>}
              </div>

              {/* Peso section */}
              <div className="pt-2 border-t border-red-100">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Scale size={12} className="text-gray-400" />
                    <span>Peso</span>
                  </div>
                  {!est && (
                    <button
                      onClick={() => iniciarEditar(lote)}
                      className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                    >
                      <Edit2 size={9} />
                      {temPeso ? 'Editar' : 'Inserir'}
                    </button>
                  )}
                </div>

                {est ? (
                  <div className="flex items-center gap-1.5 mt-1">
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={est.valor}
                      onChange={e => setPesoEdit(prev => ({
                        ...prev,
                        [lote.id]: { ...prev[lote.id], valor: e.target.value },
                      }))}
                      placeholder="kg/cab"
                      autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') salvar(lote);
                        if (e.key === 'Escape') cancelar(lote.id);
                      }}
                      className="flex-1 px-2 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                    <button
                      onClick={() => salvar(lote)}
                      disabled={est.saving}
                      className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      {est.saving
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Check size={13} />
                      }
                    </button>
                    <button
                      onClick={() => cancelar(lote.id)}
                      className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  <p className={`text-sm font-semibold ${temPeso ? 'text-blue-600' : 'text-gray-300 italic'}`}>
                    {temPeso ? fmtPesoArroba(lote.peso) : 'Não informado'}
                  </p>
                )}
              </div>

              {/* Pessoas */}
              <div className="pt-2 border-t border-red-100 space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Users size={13} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-gray-500 text-xs">Vendedor</p>
                    <p className="font-semibold text-gray-800 leading-tight">{lote.vendedor?.nome || '—'}</p>
                  </div>
                </div>
                {lote.comprador?.nome && (
                  <div className="ml-5">
                    <p className="text-gray-500 text-xs">Comprador</p>
                    <p className="font-semibold text-gray-800 leading-tight text-sm">{lote.comprador.nome}</p>
                  </div>
                )}
              </div>

              {/* Valor */}
              {valor && (
                <div className="pt-2 border-t border-red-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Valor Total</span>
                  <span className="font-bold text-green-700 text-sm">{valor}</span>
                </div>
              )}

              {/* Vender lote — apenas para status elegíveis */}
              {(lote.status === 'NOVO' || lote.status === 'EDITADO') && (
                <button
                  onClick={() => onVender(lote)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                >
                  <DollarSign size={13} />
                  Definir Valor de Venda
                </button>
              )}

              {/* Editar lote */}
              <button
                onClick={() => onEditLote(lote.id)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 rounded-lg text-xs font-semibold transition-colors"
              >
                <ExternalLink size={12} />
                Editar Lote
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Novo lote panel (curral livre) ──────────────────────────────────────────

const NovoLotePanel = ({ curral, leilaoId, onClose, usarCurralComoNumeroLote }) => {
  const handleAbrir = () => {
    const p = { leilao: leilaoId, curral: curral.display };
    if (usarCurralComoNumeroLote) p.numero_lote = curral.display;
    window.open(`/lotes/novo?${new URLSearchParams(p).toString()}`, '_blank');
  };

  return (
    <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Curral</p>
          <h3 className="text-lg font-bold text-gray-900">{curral.display}</h3>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors">
          <X size={16} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-5">
        {/* Disponível badge */}
        <div className="w-20 h-20 rounded-2xl bg-emerald-50 border-2 border-emerald-200 flex flex-col items-center justify-center gap-1">
          <span className="text-emerald-700 font-black text-2xl leading-none">{curral.display}</span>
          <span className="text-emerald-500 text-[9px] uppercase tracking-widest font-semibold">Livre</span>
        </div>

        <div className="text-center">
          <p className="text-gray-700 font-semibold text-sm">Curral disponível</p>
          <p className="text-gray-400 text-xs mt-1">
            {usarCurralComoNumeroLote
              ? <>Clique abaixo para cadastrar um lote.<br />O número do curral será pré-preenchido.</>
              : <>Clique abaixo para cadastrar um lote.<br />Número do lote não será pré-preenchido.</>
            }
          </p>
        </div>

        <button
          onClick={handleAbrir}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md"
        >
          <Plus size={16} />
          Cadastrar Lote no Curral {curral.display}
        </button>
      </div>
    </div>
  );
};

// ─── Config panel ────────────────────────────────────────────────────────────

const POLLING_OPTIONS = [
  { label: 'Desligado', value: 0 },
  { label: '10s',  value: 10  },
  { label: '15s',  value: 15  },
  { label: '30s',  value: 30  },
  { label: '1min', value: 60  },
  { label: '2min', value: 120 },
];

const ConfigPanel = ({ tempConfig, setTempConfig, tempPolling, setTempPolling, onSave, isSaving, extraCurrais }) => {
  const [newCurral, setNewCurral]         = useState('');
  const [gerarTotal, setGerarTotal]       = useState(40);
  const [gerarPrefixo, setGerarPrefixo]   = useState('');
  const inputRef = useRef(null);
  const listaRef = useRef(null);

  const addCurral = () => {
    const name = newCurral.trim().toUpperCase();
    if (!name) return;
    if (tempConfig.currais.includes(name)) {
      toast.error(`O curral ${name} já está na lista`);
    } else {
      setTempConfig(p => ({ ...p, currais: [...p.currais, name] }));
      toast.success(`Curral ${name} adicionado. Clique em "Salvar layout" para aplicar no mapa`);
      // O novo curral entra no fim da lista (que tem rolagem): leva a rolagem até ele
      setTimeout(() => {
        const el = listaRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 50);
    }
    setNewCurral('');
    inputRef.current?.focus();
  };

  const removeCurral = (name) =>
    setTempConfig(p => ({ ...p, currais: p.currais.filter(c => c !== name) }));

  const gerarSequencia = () => {
    const lista = Array.from({ length: Math.max(1, gerarTotal) }, (_, i) => {
      const pad = String(i + 1).padStart(2, '0');
      return gerarPrefixo ? `${gerarPrefixo.toUpperCase()}${pad}` : pad;
    });
    setTempConfig(p => ({ ...p, currais: lista }));
  };

  const absorverExtras = () => {
    const novos = extraCurrais.filter(k => !tempConfig.currais.includes(k));
    if (novos.length) setTempConfig(p => ({ ...p, currais: [...p.currais, ...novos] }));
  };

  return (
  <div className="w-72 bg-white border-r border-gray-200 p-5 flex flex-col gap-5 flex-shrink-0 overflow-y-auto">
    <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
      <Settings size={14} className="text-gray-500" />
      Layout do Curral
    </h3>

    {/* ── Adicionar curral manualmente ── */}
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Adicionar Curral
      </label>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          maxLength={6}
          placeholder="Ex: AB, 01, BRETE…"
          value={newCurral}
          onChange={e => setNewCurral(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') addCurral(); }}
          className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          onClick={addCurral}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all"
        >
          <Plus size={16} />
        </button>
      </div>
    </div>

    {/* ── Lista de currais ── */}
    {tempConfig.currais.length > 0 && (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Currais ({tempConfig.currais.length})
          </label>
          <button
            onClick={() => setTempConfig(p => ({ ...p, currais: [] }))}
            className="text-[10px] text-red-400 hover:text-red-600 font-medium transition-colors"
          >
            Limpar todos
          </button>
        </div>
        <div ref={listaRef} className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1 bg-gray-50 rounded-xl border border-gray-100">
          {tempConfig.currais.map(c => (
            <span
              key={c}
              className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 shadow-sm"
            >
              {c}
              <button
                onClick={() => removeCurral(c)}
                className="text-gray-300 hover:text-red-500 transition-colors ml-0.5"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      </div>
    )}

    {/* ── Absorver currais dos lotes ── */}
    {extraCurrais.length > 0 && (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1.5">
          <ListPlus size={13} />
          {extraCurrais.length} curral(is) dos lotes fora do layout
        </p>
        <p className="text-[10px] text-amber-600 mb-2">
          {extraCurrais.join(', ')}
        </p>
        <button
          onClick={absorverExtras}
          className="w-full py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-all"
        >
          Adicionar ao layout
        </button>
      </div>
    )}

    {/* ── Gerar sequência ── */}
    <div className="border-t border-gray-100 pt-4">
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Gerar sequência automática
      </label>
      <div className="flex gap-2 mb-2">
        <input
          type="number"
          min="1"
          max="200"
          value={gerarTotal}
          onChange={e => setGerarTotal(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-20 px-2 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="40"
        />
        <input
          type="text"
          maxLength={3}
          placeholder="Prefixo (opt.)"
          value={gerarPrefixo}
          onChange={e => setGerarPrefixo(e.target.value.toUpperCase())}
          className="flex-1 px-2 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <p className="text-[10px] text-gray-400 mb-2">
        {gerarPrefixo
          ? `Gera ${gerarPrefixo}01, ${gerarPrefixo}02… até ${gerarPrefixo}${String(gerarTotal).padStart(2, '0')}`
          : `Gera 01, 02… até ${String(gerarTotal).padStart(2, '0')}`}
      </p>
      <button
        onClick={gerarSequencia}
        className="w-full py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-xl text-xs font-semibold transition-all"
      >
        Substituir por sequência
      </button>
    </div>

    {/* ── Colunas ── */}
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Colunas por fileira
      </label>
      <div className="grid grid-cols-4 gap-1.5">
        {[6, 8, 10, 12, 14, 16, 18, 20].map(n => (
          <button
            key={n}
            onClick={() => setTempConfig(p => ({ ...p, colunas: n }))}
            className={`py-1.5 rounded-lg text-xs font-semibold transition-all ${
              tempConfig.colunas === n
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Atualização automática
      </label>
      <div className="grid grid-cols-3 gap-1.5">
        {POLLING_OPTIONS.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => setTempPolling(value)}
            className={`py-1.5 rounded-lg text-xs font-semibold transition-all ${
              tempPolling === value
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-1.5">
        {tempPolling > 0
          ? `Busca novos dados a cada ${tempPolling}s — ideal para TV`
          : 'Somente atualização manual'}
      </p>
    </div>

    {/* ── Usar número do curral como número do lote ── */}
    <div className="border-t border-gray-100 pt-4">
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <div
          onClick={() => setTempConfig(p => ({ ...p, usarCurralComoNumeroLote: !p.usarCurralComoNumeroLote }))}
          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
            tempConfig.usarCurralComoNumeroLote ? 'bg-emerald-500' : 'bg-gray-300'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            tempConfig.usarCurralComoNumeroLote ? 'translate-x-4' : 'translate-x-0'
          }`} />
        </div>
        <span className="text-xs font-semibold text-gray-700">
          Usar Nº do curral como Nº do lote
        </span>
      </label>
      <p className="text-[10px] text-gray-400 mt-1.5 pl-12">
        {tempConfig.usarCurralComoNumeroLote
          ? 'Pré-preenche o número do lote ao cadastrar'
          : 'Número do lote ficará em branco'}
      </p>
    </div>

    <button
      onClick={onSave}
      disabled={isSaving}
      className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2.5 rounded-xl text-sm font-semibold transition-all"
    >
      {isSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
      {isSaving ? 'Salvando…' : 'Salvar layout'}
    </button>

    {/* Legend */}
    <div className="border-t border-gray-100 pt-4 space-y-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Legenda</p>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gradient-to-br from-green-700 to-green-800 flex-shrink-0" />
        <span className="text-xs text-gray-600">Faturado</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gradient-to-br from-red-600 to-red-700 flex-shrink-0" />
        <span className="text-xs text-gray-600">Defesa</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-400 to-orange-600 flex-shrink-0" />
        <span className="text-xs text-gray-600">Negociação desfeita</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-500 to-blue-600 flex-shrink-0" />
        <span className="text-xs text-gray-600">Com peso registrado</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-gradient-to-br from-amber-500 to-amber-700 flex-shrink-0" />
        <span className="text-xs text-gray-600">Sem peso</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-200 flex-shrink-0" />
        <span className="text-xs text-gray-600">Disponível</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {[
          { color: 'bg-green-400', label: 'Faturado' },
          { color: 'bg-red-400',   label: 'Defesa'   },
          { color: 'bg-blue-300',  label: 'Novo'     },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const MapaCurralPage = () => {
  const [searchParams]       = useSearchParams();
  const leilaoId             = searchParams.get('leilao');
  const { organizadorAtivo } = useOrganizador();
  const labelCurral = useLabelCurral();

  const [lotes,           setLotes]           = useState([]);
  const [leilaoNome,      setLeilaoNome]      = useState('');
  const [leilaoData,      setLeilaoData]      = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [refreshing,      setRefreshing]      = useState(false);
  const [error,           setError]           = useState(null);
  const [isFullscreen,    setIsFullscreen]    = useState(false);
  const [showConfig,      setShowConfig]      = useState(false);
  const [lastUpdated,     setLastUpdated]     = useState(null);

  // Store only the display string so it auto-updates when lotes reload
  const [selectedDisplay, setSelectedDisplay] = useState(null);

  // Venda modal
  const [vendaModal, setVendaModal] = useState(null); // { lote }
  const [vendaValor, setVendaValor] = useState('');
  const [vendaExcecaoPorCabeca, setVendaExcecaoPorCabeca] = useState(false);
  const [meiosPagamento, setMeiosPagamento] = useState([]);

  const [pollingInterval, setPollingInterval] = useState(() => {
    const saved = localStorage.getItem('rematei-mapaCurralPolling');
    return saved ? parseInt(saved) : 30;
  });

  const [config,       setConfig]       = useState(DEFAULT_CONFIG);
  const [tempConfig,   setTempConfig]   = useState(DEFAULT_CONFIG);
  const [tempPolling,  setTempPolling]  = useState(pollingInterval);
  const [isSaving,     setIsSaving]     = useState(false);

  useEffect(() => { document.title = `Mapa do ${labelCurral} — Rematei`; }, [labelCurral]);

  // Carregar layout do backend
  useEffect(() => {
    if (!organizadorAtivo?.id) return;
    curralLayoutService.getLayout(organizadorAtivo.id)
      .then(data => {
        const usarCurralComoNumeroLote = data.usar_curral_como_numero_lote ?? true;
        if (data.currais && data.currais.length > 0) {
          const loaded = { currais: data.currais, colunas: data.colunas || 12, usarCurralComoNumeroLote };
          setConfig(loaded);
          setTempConfig(loaded);
        } else {
          // Migrar do localStorage se existir
          try {
            const old = localStorage.getItem('rematei-mapaCurralConfig');
            if (old) {
              const parsed = JSON.parse(old);
              if (parsed.totalCurrais) {
                const lista = Array.from({ length: parsed.totalCurrais }, (_, i) => {
                  const pad = String(i + 1).padStart(2, '0');
                  return parsed.prefixo ? `${parsed.prefixo}${pad}` : pad;
                });
                const migrado = { currais: lista, colunas: parsed.colunas || 12, usarCurralComoNumeroLote };
                setConfig(migrado);
                setTempConfig(migrado);
              }
            }
          } catch { /* ignore */ }
          setConfig(p => ({ ...p, usarCurralComoNumeroLote }));
          setTempConfig(p => ({ ...p, usarCurralComoNumeroLote }));
        }
      })
      .catch(() => { /* continua com default */ });
  }, [organizadorAtivo?.id]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && selectedDisplay) setSelectedDisplay(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedDisplay]);

  // ── Data fetching ──
  const carregarDados = useCallback(async (silent = false) => {
    if (!leilaoId || !organizadorAtivo?.id) return;
    if (silent) setRefreshing(true);
    else        setLoading(true);
    setError(null);
    try {
      const [lotsData, leioesData] = await Promise.all([
        lotesService.listarLotesPorLeilao(organizadorAtivo.id, leilaoId),
        leiloesService.getByOrganizador(organizadorAtivo.id),
      ]);
      setLotes(lotsData || []);
      const leilao = (leioesData || []).find(l => l.id.toString() === leilaoId);
      if (leilao) {
        const data = new Date(leilao.data_leilao).toLocaleDateString('pt-BR');
        setLeilaoNome(`${leilao.nome} — ${data}`);
        setLeilaoData(leilao);
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      if (!silent)
        setError('Não foi possível carregar os dados. Verifique a conexão e tente novamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [leilaoId, organizadorAtivo]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // Carregar meios de pagamento
  useEffect(() => {
    if (!organizadorAtivo?.id) return;
    meiosPagamentoService.listar(organizadorAtivo.id)
      .then(meios => setMeiosPagamento(meios || []))
      .catch(() => setMeiosPagamento([]));
  }, [organizadorAtivo]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'rematei-lote-updated') carregarDados(true);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [carregarDados]);

  useEffect(() => {
    if (!pollingInterval) return;
    const id = setInterval(() => carregarDados(true), pollingInterval * 1000);
    return () => clearInterval(id);
  }, [pollingInterval, carregarDados]);

  // ── Actions ──
  const handleSavePeso = useCallback(async (loteId, peso) => {
    try {
      await lotesService.atualizarPeso(organizadorAtivo.id, loteId, peso);
      await carregarDados(true);
    } catch (e) {
      console.error('Erro ao salvar peso:', e);
    }
  }, [organizadorAtivo, carregarDados]);

  const handleEditLote = useCallback((loteId) => {
    window.open(`/lotes/editar/${loteId}`, '_blank');
  }, []);

  const handleVender = useCallback((lote) => {
    setVendaValor('');
    setVendaExcecaoPorCabeca(false);
    setVendaModal(lote);
  }, []);

  const confirmarVenda = () => {
    if (!vendaModal || !vendaValor || parseFloat(vendaValor) <= 0) return;
    const valorUnit = parseFloat(vendaValor);
    const isPorParcela = leilaoData?.regra_faturamento === 'por_parcela' && !vendaExcecaoPorCabeca;
    let valorTotalPrecalculado = valorUnit * (vendaModal.qtd || 1);
    if (isPorParcela && leilaoData?.meio_pagamento_padrao_id) {
      const meio = meiosPagamento.find(m => m.id === leilaoData.meio_pagamento_padrao_id);
      const qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
      valorTotalPrecalculado = valorUnit * qtdParcelas * (vendaModal.qtd || 1);
    }
    const params = new URLSearchParams({
      step: '4',
      valor_venda: String(valorUnit),
      valor_total_precalculado: String(valorTotalPrecalculado),
    });
    if (vendaExcecaoPorCabeca) {
      params.set('excecao_por_cabeca', '1');
    }
    window.open(`/lotes/editar/${vendaModal.id}?${params.toString()}`, '_blank');
    setVendaModal(null);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // ── Curral matching ──
  const normKey = useCallback((curral) => {
    if (!curral) return null;
    const s   = curral.trim().toUpperCase();
    const num = parseInt(s, 10);
    return isNaN(num) ? s : num;
  }, []);

  const curralToLotes = useMemo(() => {
    const map = new Map();
    lotes.forEach(lote => {
      const key = normKey(lote.curral);
      if (key === null) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(lote);
    });
    return map;
  }, [lotes, normKey]);

  const curraisGrid = useMemo(() => {
    const grid     = [];
    const usedKeys = new Set();

    (config.currais || []).forEach(name => {
      const display    = name.toUpperCase();
      const num        = parseInt(display, 10);
      const candidates = isNaN(num)
        ? [display, display.toLowerCase()]
        : [num, display, String(num).padStart(2, '0'), String(num)];

      let lotsHere = [];
      for (const k of candidates) {
        if (curralToLotes.has(k)) {
          lotsHere = curralToLotes.get(k);
          usedKeys.add(k);
          break;
        }
      }
      grid.push({ display, ocupado: lotsHere.length > 0, lotes: lotsHere });
    });

    // Lotes com curral não mapeado aparecem ao final
    curralToLotes.forEach((lots, key) => {
      if (!usedKeys.has(key)) {
        grid.push({ display: String(key), ocupado: true, lotes: lots, extra: true });
      }
    });

    return grid;
  }, [config.currais, curralToLotes]);

  // Currais dos lotes que não estão no layout salvo
  const extraCurrais = useMemo(() => {
    const saved = new Set((config.currais || []).map(c => c.toUpperCase()));
    return Array.from(curralToLotes.keys())
      .map(k => String(k).toUpperCase())
      .filter(k => !saved.has(k));
  }, [config.currais, curralToLotes]);

  // Derive selectedCurral live from curraisGrid so peso updates are reflected instantly
  const selectedCurral = useMemo(
    () => selectedDisplay ? (curraisGrid.find(c => c.display === selectedDisplay) || null) : null,
    [curraisGrid, selectedDisplay]
  );

  const stats = useMemo(() => {
    const ocupados = curraisGrid.filter(c => c.ocupado).length;
    const total    = curraisGrid.length;
    return {
      total,
      ocupados,
      livres: total - ocupados,
      pct:    total > 0 ? Math.round((ocupados / total) * 100) : 0,
    };
  }, [curraisGrid]);

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      await curralLayoutService.saveLayout(organizadorAtivo.id, {
        currais: tempConfig.currais,
        colunas: tempConfig.colunas,
        usar_curral_como_numero_lote: tempConfig.usarCurralComoNumeroLote ?? true,
      });
    } catch (e) {
      console.error('Erro ao salvar layout:', e);
    } finally {
      setIsSaving(false);
    }
    setConfig(tempConfig);
    setPollingInterval(tempPolling);
    localStorage.setItem('rematei-mapaCurralPolling', String(tempPolling));
    setShowConfig(false);
  };

  const handleToggleConfig = () => {
    setTempConfig(config);
    setShowConfig(s => !s);
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="p-4 bg-slate-800 rounded-2xl">
          <LayoutGrid size={32} className="text-white animate-pulse" />
        </div>
        <p className="text-gray-500 text-sm">Carregando mapa do {labelCurral.toLowerCase()}…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex flex-col items-center justify-center gap-4">
        <AlertCircle size={40} className="text-red-400" />
        <p className="text-gray-600 text-sm max-w-xs text-center">{error}</p>
        <button
          onClick={carregarDados}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all"
        >
          <RefreshCw size={15} />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-50 overflow-hidden">

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-5 py-4 flex items-center gap-3 flex-shrink-0 shadow-lg">
        <div className="p-2 bg-white/10 rounded-xl">
          <LayoutGrid size={20} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-white leading-tight">Mapa do {labelCurral}</h1>
            {pollingInterval > 0 && (
              <span className="flex items-center gap-1 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Ao Vivo
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {leilaoNome && <p className="text-slate-400 text-xs truncate">{leilaoNome}</p>}
            {lastUpdated && (
              <p className="text-slate-500 text-xs whitespace-nowrap">
                Atualizado às {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={() => carregarDados(false)}
          title="Atualizar agora"
          className="p-2 hover:bg-white/10 rounded-xl text-white/70 hover:text-white transition-all"
        >
          <RefreshCw size={17} className={refreshing ? 'animate-spin' : ''} />
        </button>

        <button
          onClick={handleToggleConfig}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
            showConfig
              ? 'bg-white text-slate-800'
              : 'bg-white/10 hover:bg-white/20 text-white'
          }`}
        >
          <Settings size={15} />
          <span className="hidden sm:inline">Configurar</span>
        </button>

        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/20 text-white transition-all"
        >
          {isFullscreen
            ? <><Minimize2 size={15} /><span className="hidden sm:inline">Sair</span></>
            : <><Maximize2 size={15} /><span className="hidden sm:inline">Tela Cheia</span></>
          }
        </button>
      </div>

      {/* ── Stats bar ── */}
      <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center gap-5 flex-shrink-0 flex-wrap">
        <StatBadge color="bg-gray-400 ring-gray-200"       label="Total"    value={stats.total}    textColor="text-gray-900"    />
        <StatBadge color="bg-red-500 ring-red-100"         label="Ocupados" value={stats.ocupados} textColor="text-red-600"     />
        <StatBadge color="bg-emerald-500 ring-emerald-100" label="Livres"   value={stats.livres}   textColor="text-emerald-600" />

        {/* Legenda de cores */}
        <div className="hidden lg:flex items-center gap-4 ml-4 pl-4 border-l border-gray-200">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-green-700 to-green-800" />
            <span className="text-[10px] text-gray-500">Faturado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-red-600 to-red-700" />
            <span className="text-[10px] text-gray-500">Defesa</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-blue-500 to-blue-600" />
            <span className="text-[10px] text-gray-500">Com peso</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-amber-500 to-amber-700" />
            <span className="text-[10px] text-gray-500">Sem peso</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
            <span className="text-[10px] text-gray-500">Livre</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-3 ml-auto max-w-xs flex-1">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all duration-500"
              style={{ width: `${stats.pct}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-500 w-10 text-right tabular-nums">
            {stats.pct}%
          </span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {showConfig && (
          <ConfigPanel
            tempConfig={tempConfig}
            setTempConfig={setTempConfig}
            tempPolling={tempPolling}
            setTempPolling={setTempPolling}
            onSave={saveConfig}
            isSaving={isSaving}
            extraCurrais={extraCurrais}
          />
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {curraisGrid.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <LayoutGrid size={48} className="mb-3 opacity-25" />
              <p className="text-lg font-medium text-gray-500">Nenhum curral configurado</p>
              <p className="text-sm mt-1 text-gray-400">
                Clique em <strong>Configurar</strong> para definir o layout
              </p>
            </div>
          ) : (
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${config.colunas}, minmax(0, 1fr))` }}
            >
              {curraisGrid.map((curral) => (
                <CurralCard
                  key={curral.display}
                  curral={curral}
                  selected={selectedDisplay === curral.display}
                  onClick={() => setSelectedDisplay(
                    prev => prev === curral.display ? null : curral.display
                  )}
                  labelCurral={labelCurral}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail panel — ocupado */}
        {selectedCurral?.ocupado && (
          <DetalhePanel
            curral={selectedCurral}
            onClose={() => setSelectedDisplay(null)}
            onSavePeso={handleSavePeso}
            onEditLote={handleEditLote}
            onVender={handleVender}
          />
        )}

        {/* Novo lote panel — livre */}
        {selectedCurral && !selectedCurral.ocupado && (
          <NovoLotePanel
            curral={selectedCurral}
            leilaoId={leilaoId}
            onClose={() => setSelectedDisplay(null)}
            usarCurralComoNumeroLote={config.usarCurralComoNumeroLote ?? true}
          />
        )}
      </div>

      {/* ── Modal de Venda ── */}
      {vendaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Definir Valor de Venda</h2>
                <p className="text-sm text-gray-500 mt-0.5">Lote {vendaModal.numero_lote} — {vendaModal.qtd} {vendaModal.qtd === 1 ? 'animal' : 'animais'}</p>
              </div>
              <button onClick={() => { setVendaModal(null); setVendaExcecaoPorCabeca(false); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Toggle exceção */}
              {leilaoData?.regra_faturamento === 'por_parcela' && (
                <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={vendaExcecaoPorCabeca}
                    onChange={(e) => setVendaExcecaoPorCabeca(e.target.checked)}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-amber-800 font-medium">Exceção: faturar por cabeça neste lote</span>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {(leilaoData?.regra_faturamento === 'por_parcela' && !vendaExcecaoPorCabeca)
                    ? 'Valor por Parcela (R$)'
                    : 'Valor por Cabeça (R$)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={vendaValor}
                  onChange={e => setVendaValor(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmarVenda(); if (e.key === 'Escape') { setVendaModal(null); setVendaExcecaoPorCabeca(false); } }}
                  placeholder="0,00"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  autoFocus
                />
              </div>
              {vendaValor && parseFloat(vendaValor) > 0 && (() => {
                const isPorParcela = leilaoData?.regra_faturamento === 'por_parcela' && !vendaExcecaoPorCabeca;
                const valorUnit = parseFloat(vendaValor);
                const qtd = vendaModal?.qtd || 1;

                let qtdParcelas = 1;
                let valorTotal = valorUnit * qtd;
                if (isPorParcela && leilaoData?.meio_pagamento_padrao_id) {
                  const meio = meiosPagamento.find(m => m.id === leilaoData.meio_pagamento_padrao_id);
                  qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
                  valorTotal = valorUnit * qtdParcelas * qtd;
                }

                return (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-1">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Quantidade</span>
                      <span className="font-medium">{qtd} {qtd === 1 ? 'animal' : 'animais'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{isPorParcela ? 'Valor por Parcela' : 'Valor unitário'}</span>
                      <span className="font-medium">R$ {valorUnit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {isPorParcela && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Unidades (c/ peso)</span>
                        <span className="font-medium">{qtdParcelas}x</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-yellow-800 border-t border-yellow-200 pt-2 mt-1">
                      <span>Valor Total</span>
                      <span>R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => { setVendaModal(null); setVendaExcecaoPorCabeca(false); }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                disabled={!vendaValor || parseFloat(vendaValor) <= 0}
                onClick={confirmarVenda}
                className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                Ir para Financeiro
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Helper ──────────────────────────────────────────────────────────────────

const StatBadge = ({ color, label, value, textColor }) => (
  <div className="flex items-center gap-2">
    <div className={`w-2.5 h-2.5 rounded-full ring-2 ${color}`} />
    <span className="text-sm text-gray-600">
      {label}: <span className={`font-bold ${textColor}`}>{value}</span>
    </span>
  </div>
);

export default MapaCurralPage;
